import { computed, defineComponent, h, nextTick, reactive, ref, toRef, unref, watch, watchEffect } from 'vue';

import { classNames, omit, scrollTo, warn } from '@components/utils';

import {
  makeDisabledProps,
  makeLoadingProps,
  makeLocaleProps,
  makeReadonlyProps,
  makeSizeProps,
  makeThemeProps,
  provideTheme,
  useDisplay,
  useLocale,
  useRender
} from '../../../composable';
import { CdsLoader } from '../../CdsLoader';
import {
  ASCEND,
  DEFAULT_PAGE_SIZE,
  DESCEND,
  getFilterData,
  getPaginationParam,
  getSortData,
  makeTableProps,
  useColumns,
  useFilter,
  useLazyKVMap,
  usePagination,
  useRenderPagination,
  useSelection,
  useSorter,
  useTitleColumns
} from '../composable';
import { INTERNAL_HOOKS, TABLE_INDENT_SIZE } from '../constants';
import { useProvideSlots, useProvideTableContext } from '../context';

import { renderExpandIcon } from './Icons/ExpandIcon';
import InternalTable from './Table/InternalTable';

import type { IContextSlots } from '../context';
import type { NTable } from '../interface';
import type { PropType } from 'vue';

const componentName = 'TableProvider';
export default defineComponent({
  name: componentName,
  inheritAttrs: false,
  props: {
    ...makeTableProps(),
    ...makeSizeProps(),
    ...makeLocaleProps(),
    ...makeLoadingProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeThemeProps(),

    contextSlots: {
      type: Object as PropType<IContextSlots>,
      required: true
    }
  },
  emits: {
    change: (
      pagination: NTable.NPagination.IProps,
      filters: Record<string, NTable.TFilterValue | null>,
      sorter: NTable.ISorterResult | NTable.ISorterResult[],
      extra: NTable.ICurrentData
    ) => true,
    expand: (expanded: boolean, record: NTable.TDefaultRecord) => true,
    expandedRowsChange: (expandedKeys: NTable.TKey) => true,
    resizeColumn: (width: number, column: NTable.NColumn.IColumn) => true,
    'update:expandedRowKeys': (expandedKeys: NTable.TKey[]) => true
  },
  setup(props, { attrs, slots, emit }) {
    const { themeClasses } = provideTheme(props);

    useProvideSlots(computed(() => props.contextSlots));
    useProvideTableContext({
      onResizeColumn: (width, column) => {
        emit('resizeColumn', width, column);
      }
    });

    const display = useDisplay();

    const mergedColumns = computed(() => {
      return props.columns?.filter(
        (column: NTable.NColumn.IColumn<NTable.TDefaultRecord>) =>
          !column.responsive || column.responsive.some((breakpoint) => display[breakpoint].value)
      ) as NTable.NColumn.TColumns;
    });

    const rawData = computed(() => props.data || []);
    const childrenColumnName = computed(() => props.childrenColumnName || 'children');

    const expandType = computed<NTable.NExpand.TExpandableType>(() => {
      if (rawData.value.some((item) => item?.[childrenColumnName.value])) return 'nest';
      if (props.expandedRowRender) return 'row';
      return false;
    });

    const internalRefs = reactive({ body: null });
    const updateInternalRefs = (refs: any) => {
      Object.assign(internalRefs, refs);
    };

    const locale = useLocale(props);
    const tableLocale = ref(locale.table);

    const transformCellText = computed(() => props.transformCellText);

    // ============================ RowKey ============================
    const getRowKey = computed<NTable.NRow.TGetRowKey<NTable.TDefaultRecord>>(() => {
      if (typeof props.rowKey === 'function') return props.rowKey;
      return (record) => (record as any)?.[props.rowKey as string];
    });

    const { getRecordByKey } = useLazyKVMap(rawData, childrenColumnName, getRowKey);
    // ============================ RowKey ============================

    // ============================ Events =============================
    const changeEventInfo: Partial<NTable.IChangeEventInfo> = {};

    const triggerOnChange = (info: Partial<NTable.IChangeEventInfo>, action: NTable.TAction, reset = false) => {
      const { pagination, scroll, onChange } = props;
      const changeInfo = {
        ...changeEventInfo,
        ...info
      };

      if (reset) {
        changeEventInfo.resetPagination!();

        if (changeInfo.pagination!.modelValue) {
          changeInfo.pagination!.modelValue = 1;
        }

        if (pagination && pagination.modelValue) {
          pagination.modelValue = 1;
          pagination.length = changeInfo.pagination!.length;
        }
      }

      if (scroll && scroll.scrollToFirstRowOnChange !== false && internalRefs.body) {
        scrollTo(0, {
          getContainer: () => internalRefs.body as unknown as HTMLElement
        });
      }

      onChange?.(changeInfo.pagination!, changeInfo.filters!, changeInfo.sorter!, {
        currentData: getFilterData(
          getSortData(rawData.value, changeInfo.sorterStates!, childrenColumnName.value),
          changeInfo.filterStates!
        ),
        action
      });
    };
    // ============================ Events =============================

    // ============================ Sorter =============================
    const onSorterChange = (
      sorter: NTable.ISorterResult | NTable.ISorterResult[],
      sorterStates: NTable.ISortState[]
    ) => {
      triggerOnChange({ sorter, sorterStates }, 'sort', false);
    };

    const sortDirections = computed<NTable.TSortOrder[]>(() => props.sortDirection || [ASCEND, DESCEND]);
    const showSorterTooltip = toRef<any, any>(props, 'showSorterTooltip');

    const {
      transformColumns: transformSorterColumns,
      mergedSorterStates: sortStates,
      columnTitleSorterProps,
      sorters
    } = useSorter({
      mergedColumns,
      onSorterChange,
      sortDirections,
      tableLocale,
      showSorterTooltip
    });

    const sortedData = computed(() => getSortData(rawData.value, sortStates.value, childrenColumnName.value));
    // ============================ Sorter =============================

    // ============================ Filter ============================
    const onFilterChange = (
      filters: Record<string, NTable.TFilterValue | null>,
      filterStates: NTable.IFilterState[]
    ) => {
      triggerOnChange({ filters, filterStates }, 'filter', true);
    };

    const getPopupContainer = toRef<any, any>(props, 'getPopupContainer');

    const {
      transformColumns: transformFilterColumns,
      mergedFilterStates: filterStates,
      filters
    } = useFilter({
      mergedColumns,
      onFilterChange,
      tableLocale,
      getPopupContainer
    });

    const mergedData = computed(() => getFilterData(sortedData.value, filterStates.value));
    // ============================ Filter ============================

    // ============================ Column ============================
    const { filledColumns: transformBasicColumn } = useColumns(toRef(props, 'contextSlots'));

    const columnTitleProps = computed(() => ({
      ...columnTitleSorterProps.value
    }));
    const { filledColumns: transformTitleColumns } = useTitleColumns(columnTitleProps);
    // ============================ Column ============================

    // ========================== Pagination ==========================
    const onPaginationChange = (modelValue: number | undefined, length: number) => {
      triggerOnChange({ pagination: { ...changeEventInfo.pagination, modelValue, length } }, 'paginate');
    };

    const { mergedPagination, resetPagination } = usePagination(
      computed(() => mergedData.value.length),
      toRef(props, 'pagination'),
      toRef(props, 'disabled'),
      onPaginationChange
    );

    watchEffect(() => {
      changeEventInfo.sorter = sorters.value;
      changeEventInfo.sorterStates = sortStates.value;

      changeEventInfo.filters = filters.value;
      changeEventInfo.filterStates = filterStates.value;
      changeEventInfo.pagination =
        props.pagination === false ? {} : getPaginationParam(props.pagination, mergedPagination.value);
      changeEventInfo.resetPagination = resetPagination;
    });
    // ========================== Pagination ==========================

    // ============================= Data =============================
    const pageData = computed(() => {
      if (props.pagination === false || !mergedPagination.value.length) {
        return mergedData.value;
      }

      const { modelValue: current = 1, length = DEFAULT_PAGE_SIZE, total } = mergedPagination.value;

      if (mergedData.value.length < total!) {
        if (mergedData.value.length > +length) {
          return mergedData.value.slice((current - 1) * (total as number), current * (total as number));
        }
        return mergedData.value;
      }

      return mergedData.value.slice((current - 1) * (total as number), current * (total as number));
    });

    watchEffect(
      () => {
        nextTick(() => {
          const { total, length = DEFAULT_PAGE_SIZE } = mergedPagination.value;

          if (mergedData.value.length < total!) {
            if (mergedData.value.length < +length) {
              warn(
                componentName,
                'Property `data` length is less than `pagination.total` but large than `pagination.length`. Place make sure your config correct data witch async mode.'
              );
            }
          }
        });
      },
      { flush: 'post' }
    );

    const expandIconColumnIndex = computed(() => {
      if (props.showExpandColumn === false) return -1;

      if (expandType.value === 'nest' && props.expandIconColumnIndex === undefined) {
        return props.rowSelection ? 1 : 0;
      } else if (props.expandIconColumnIndex! > 0 && props.rowSelection) {
        return props.expandIconColumnIndex! - 1;
      }

      return props.expandIconColumnIndex;
    });

    const rowSelection = ref();
    watch(
      () => props.rowSelection,
      () => {
        rowSelection.value = props.rowSelection ? { ...props.rowSelection } : props.rowSelection;
      },
      { deep: true, immediate: true }
    );
    // ============================= Data =============================

    // ========================== Selections ==========================
    const { transformColumns: transformSelectionColumns, selectedKeySet } = useSelection(rowSelection, {
      data: mergedData,
      pageData,
      getRowKey,
      getRecordByKey,
      expandType,
      childrenColumnName,
      locale: tableLocale,
      getPopupContainer: computed(() => props.getPopupContainer)
    });

    const internalRowClassName = (record: any, index: number, indent: number) => {
      let mergedRowClassName;
      const { rowClassName } = props;

      if (typeof rowClassName === 'function') {
        mergedRowClassName = classNames(rowClassName(record, index, indent));
      } else {
        mergedRowClassName = classNames(rowClassName);
      }

      return classNames(
        {
          ['cds-table__row--selected']: selectedKeySet.value.has(getRowKey.value(record, index))
        },
        mergedRowClassName
      );
    };

    const indentSize = computed(() => {
      return typeof props.indentSize === 'number' ? props.indentSize : TABLE_INDENT_SIZE;
    });

    const transformColumns = (
      innerColumns: NTable.NColumn.TColumns<NTable.TDefaultRecord>
    ): NTable.NColumn.TColumns<NTable.TDefaultRecord> => {
      return transformTitleColumns(
        transformSelectionColumns(transformFilterColumns(transformSorterColumns(transformBasicColumn(innerColumns))))
      );
    };
    // ========================== Selections ==========================

    // ============================ Render ============================
    useRender(() => {
      const {
        expandIcon = slots.expandIcon || renderExpandIcon(tableLocale.value, props.size),
        pagination,
        loading,
        hovered,
        bordered,
        borderless,
        disabled,
        readonly,
        size,
        expandedRowKeys,
        defaultExpandedRowKeys
      } = props;
      const tableProps = omit(props, ['columns']);
      const { topPaginationNode, bottomPaginationNode } = useRenderPagination(
        mergedPagination,
        pagination,
        tableLocale,
        triggerOnChange
      );

      return h(
        'div',
        {
          class: classNames(
            'cds-table-wrapper',
            { 'cds-table--with-pagination': !!bottomPaginationNode || !!topPaginationNode },
            unref(themeClasses),
            attrs.class
          ),
          style: attrs.style
        },
        [
          !unref(pageData).length &&
            loading &&
            h(CdsLoader, { overlay: true, fixed: true, class: 'cds-table__loading' }),
          topPaginationNode,
          h(
            InternalTable,
            {
              ...attrs,
              ...tableProps,
              data: pageData.value,
              columns: mergedColumns.value,
              rowKey: getRowKey.value,
              rowClassName: internalRowClassName,
              indentSize: indentSize.value,
              expandIconColumnIndex: expandIconColumnIndex.value,
              expandedRowKeys,
              defaultExpandedRowKeys,
              expandIcon,

              class: classNames({
                [`cds-table__size--${size}`]: size,
                [`cds-table--bordered`]: bordered,
                [`cds-table--borderless`]: borderless,
                [`cds-table--hovered`]: hovered,
                [`cds-table--disabled`]: disabled,
                [`cds-table--readonly`]: readonly
              }),

              // Internal
              transformCellText: transformCellText.value,
              internalHooks: INTERNAL_HOOKS,
              onUpdateInternalRefs: updateInternalRefs,
              internalRefs,
              transformColumns
            },
            {
              ...slots,
              emptyText: () => slots.emptyText?.() || tableLocale.value.emptyText
            }
          ),
          bottomPaginationNode
        ]
      );
    });

    return { selectedKeySet };
  }
});
